// The timeline episode (v8): the voices set the timing, faces belong to pictures. See docs/lines-v8.md.
//   - An episode is an ordered list of lines (spoken: typed or recorded; or silent) played back to back: no gaps, no overlaps.
//   - Picture, camera and feeling are markers that change state "from here on". Each is anchored to a line
//     ({ line, at: seconds into it }), so re-recording or reordering lines moves them along.
//   - Every picture has at least one line: a new picture at a boundary brings a 1 s silent placeholder line.
// Everything here is pure: `resolve` turns anchors into absolute times (memoised per episode object), the readers sit on
// it, and edits return a new episode without touching the old one. Times are seconds from the episode start.
import type { Camera, Picture, PictureVersion, Take } from "./episode";
import type { Cue, Emotion, FaceSpots } from "./face";
import { uid } from "./ids";

export const TIMELINE_VERSION = 8;
const SAME = 0.05; // two markers of one kind this close are the same moment
const EPS = 1e-6;
const WHOLE_CAMERA: Camera = { cx: 0.5, cy: 0.5, zoom: 1 };
export const NEW_SILENCE = 2; // default length of a new silent line, and the display length of an episode with pictures but no lines
export const MIN_SILENCE = 0.5;
export const MAX_SILENCE = 30;
export const PLACEHOLDER_SECONDS = 1; // the silent line a new picture comes with

export type PaintSource = { src: string; name: string; kind: "character" | "background" | "prop" | "shot" };
export type Framing = "whole" | "medium" | "close" | "free"; // free: framed by hand, the marker's camera as placed

export type Anchor = { line: string | null; at: number }; // line null: before any line (time 0 of an empty episode)

// A line is spoken (typed for an AI voice, or recorded) or silent. A silent line may have nobody speaking.
export type Line = {
  id: string;
  how: "typed" | "recorded" | "silent";
  char?: string; // who speaks
  status: "syncing" | "ready" | "error"; error?: string;
  text?: string; direction?: string;
  audio?: string; duration: number; cues: Cue[];
  takes?: Take[];
  placeholder?: true; // the 1 s silence a new picture came with, untouched since
};

// A character's face on a picture, in the pixels of that picture. It shows for as long as the picture does.
export type PlacedFace = { char: string; face: FaceSpots };
// One painted frame of an animation: its picture, the faces found on it from the dots (or copied from the picture),
// and faces fixed by hand (which correct the found ones; see animFaces).
export type AnimFrame = { picture: Picture; found: PlacedFace[]; placed?: PlacedFace[] };
// A stop-motion animation that plays inside one picture. `frames` are the frames after the picture itself, which is frame 0.
export type PictureMotion = {
  id: string; made: number; model?: string;
  prompt: string; dots: boolean; painted: "loop" | "once"; // how it was asked for
  frames: AnimFrame[];
  play: "loop" | "once" | "stretch"; fps: number; // how it plays (fps unused for stretch)
};
export type PictureMark = {
  id: string; anchor: Anchor; picture: Picture;
  motions?: PictureMotion[]; motionId?: string; // every animation made for this picture, and the one in use (none: the still)
  faces: PlacedFace[]; // who is on screen with this picture, and where
  prompt: string; // last description used
  refs: PaintSource[]; // images it was painted from
  style?: string; // the drawing style's words it was painted in; old pictures have none
  versions: PictureVersionFaces[]; // every picture made for this marker, oldest first; `picture` is the one in use
  boil?: false; // false: no wiggle over this picture (still or animated); absent: the episode's wiggle
};
// A version's faces: where they were found when it was painted, or placed in the image maker. Put on the timeline,
// they become the marker's `faces`.
export type PictureVersionFaces = PictureVersion & { faces?: PlacedFace[] };
export type CameraMark = { id: string; anchor: Anchor; framing: Framing; on?: string; camera: Camera }; // camera = resolved {cx,cy,zoom}; on = character id
export type FeelingMark = { id: string; anchor: Anchor; char: string; emotion: Emotion };

export type TimelineEpisode = {
  version: 8; kind: "timeline";
  id: string; title: string;
  lines: Line[]; pictures: PictureMark[]; camera: CameraMark[]; feelings: FeelingMark[];
  wiggle: number; // whole-episode boil
  createdAt: number; updatedAt: number;
  demo?: true; // the visitor's "Try Sketchy" episode (src/server/demo.ts): opened in the demo editor, kept off the home list
  rev?: number; // counts saves; a save must say which one it started from (src/server/routes/episodes.ts)
};

// Same value as DEFAULT_WIGGLE in src/engine/frame.ts (kept here so the model and the server need no engine imports).
export const TIMELINE_WIGGLE = 3.2;

export const isTimelineEpisode = (x: unknown): x is TimelineEpisode =>
  typeof x === "object" && x !== null && (x as { version?: unknown }).version === TIMELINE_VERSION && (x as { kind?: unknown }).kind === "timeline";

export function newTimelineEpisode(title: string): TimelineEpisode {
  const now = Date.now();
  return {
    version: 8, kind: "timeline", id: uid("ep"), title,
    lines: [],
    pictures: [], camera: [], feelings: [], wiggle: TIMELINE_WIGGLE, createdAt: now, updatedAt: now,
  };
}

// ---------------------------------------------------------------- the resolved view

export type PlacedLine = Line & { start: number; end: number };
// A marker with its absolute time `t` and where it stops applying (`end`: the next marker of its kind, else the episode end).
export type Placed<M> = M & { t: number; end: number };
// A face on screen at some moment: the picture mark it belongs to, whose it is and where it sits.
export type OnFace = { id: string; char: string; face: FaceSpots };

export type Resolved = {
  lines: PlacedLine[];
  duration: number; // real length: the sum of the lines
  display: number; // length to show and scrub: the real length, or NEW_SILENCE when there are pictures but no lines
  pictures: Placed<PictureMark>[]; camera: Placed<CameraMark>[]; feelings: Placed<FeelingMark>[];
  cast: string[]; // characters in order of their first line or first appearance in a picture's faces
  line: Map<string, PlacedLine>;
};

const cache = new WeakMap<TimelineEpisode, Resolved>();

export function resolve(ep: TimelineEpisode): Resolved {
  const hit = cache.get(ep);
  if (hit) return hit;
  let at = 0;
  const lines = ep.lines.map((l): PlacedLine => { const start = at; at += l.duration; return { ...l, start, end: at }; });
  const duration = at;
  const display = duration > 0 ? duration : ep.pictures.length ? NEW_SILENCE : 0;
  const byId = new Map(lines.map(l => [l.id, l]));
  const timeOf = (a: Anchor) => {
    const l = a.line === null ? undefined : byId.get(a.line); // line null is always time 0
    return l ? l.start + Math.max(0, Math.min(a.at, l.duration)) : 0;
  };
  const place = <M extends { anchor: Anchor }>(list: M[]) => list.map(m => ({ ...m, t: timeOf(m.anchor), end: display })).sort((a, b) => a.t - b.t);
  const pictures = place(ep.pictures), camera = place(ep.camera), feelings = place(ep.feelings);
  const spanTo = <M extends { t: number; end: number }>(list: M[], same: (a: M, b: M) => boolean = () => true, stops: (m: M) => number = () => Infinity) => {
    list.forEach((m, i) => {
      const next = list.slice(i + 1).find(o => same(m, o));
      m.end = Math.max(m.t, Math.min(next ? next.t : display, stops(m), display));
    });
  };
  if (pictures[0]) pictures[0].t = 0; // the first picture shows from the very start
  spanTo(pictures);
  spanTo(camera, undefined, m => pictures.find(p => p.t > m.t + EPS)?.t ?? Infinity); // a camera stops at the next picture
  spanTo(feelings, (a, b) => a.char === b.char);
  // Cast: first line or first appearance in a picture's faces, by time.
  const first = new Map<string, number>();
  const note = (char: string, t: number) => { if (!first.has(char) || t < first.get(char)! - EPS) first.set(char, t); };
  for (const l of lines) if (l.how !== "silent" && l.char) note(l.char, l.start);
  for (const p of pictures) for (const f of p.faces) note(f.char, p.t);
  const cast = [...first.keys()].sort((a, b) => first.get(a)! - first.get(b)!);
  const r: Resolved = { lines, duration, display, pictures, camera, feelings, cast, line: byId };
  cache.set(ep, r);
  return r;
}

// ---------------------------------------------------------------- readers

export const duration = (ep: TimelineEpisode) => resolve(ep).duration;
// The length the editor shows and scrubs: `duration`, or 2 s when there are pictures but no lines yet.
export const displayDuration = (ep: TimelineEpisode) => resolve(ep).display;
// Play and export need at least one line.
export const canPlay = (ep: TimelineEpisode) => ep.lines.length > 0 && ep.pictures.length > 0;
// How many frames an export has at `fps` (at least one).
export const exportFrames = (ep: TimelineEpisode, fps: number) => Math.max(1, Math.ceil(duration(ep) * fps));
export const castOf = (ep: TimelineEpisode) => resolve(ep).cast;

export const isSilent = (l: { how: Line["how"] }) => l.how === "silent";
export const lineAtTime = (ep: TimelineEpisode, t: number): PlacedLine | undefined => resolve(ep).lines.find(l => t >= l.start - EPS && t < l.end - EPS);
export const lineById = (ep: TimelineEpisode, id: string): PlacedLine | undefined => resolve(ep).line.get(id);
// The lines someone speaks (not the silent ones), in time order.
export const spokenLines = (ep: TimelineEpisode) => resolve(ep).lines.filter(l => l.how !== "silent");
export const linesOf = (ep: TimelineEpisode, char: string) => spokenLines(ep).filter(l => l.char === char);
export const lineAt = (ep: TimelineEpisode, char: string, t: number): PlacedLine | undefined => {
  const l = lineAtTime(ep, t);
  return l && l.how !== "silent" && l.char === char ? l : undefined;
};
// The lines playing while a picture shows (any overlap with its time on screen), in time order.
export const linesUnder = (ep: TimelineEpisode, pictureId: string): PlacedLine[] => {
  const p = resolve(ep).pictures.find(m => m.id === pictureId);
  return p ? resolve(ep).lines.filter(l => l.end > p.t + EPS && l.start < p.end - EPS) : [];
};
// The untouched placeholder line that is a picture's only line, if that is what it has.
export function placeholderOf(ep: TimelineEpisode, pictureId: string): PlacedLine | undefined {
  const under = linesUnder(ep, pictureId);
  return under.length === 1 && under[0]!.placeholder ? under[0] : undefined;
}

// The last marker at or before t. Before the first one there is none.
function latest<M extends { t: number }>(list: M[], t: number): M | undefined {
  let found: M | undefined;
  for (const m of list) if (m.t <= t + EPS) found = m; else break;
  return found;
}
export const pictureAt = (ep: TimelineEpisode, t: number): Placed<PictureMark> | undefined => latest(resolve(ep).pictures, t);

// The camera marker at t. With none yet (or before the first), the whole picture, as a marker with id "default".
export const DEFAULT_CAMERA: Placed<CameraMark> = { id: "default", anchor: { line: null, at: 0 }, t: 0, end: 0, framing: "whole", camera: WHOLE_CAMERA };
// A camera set on one picture stops where the next picture starts: a new picture opens on the whole picture.
export const cameraAt = (ep: TimelineEpisode, t: number): Placed<CameraMark> => {
  const cam = latest(resolve(ep).camera, t);
  const pic = pictureAt(ep, t);
  return cam && !(pic && cam.t < pic.t - EPS) ? cam : DEFAULT_CAMERA;
};

// The picture showing at t and its faces: the animation's frame if the picture plays one, else the picture itself.
// `mark.end` is where the picture stops showing.
export type Shown = { mark: Placed<PictureMark>; picture: Picture; faces: PlacedFace[]; frame: number };
export function shownAt(ep: TimelineEpisode, t: number): Shown | undefined {
  const mark = pictureAt(ep, t);
  if (!mark) return undefined;
  const motion = motionOf(mark);
  if (!motion) return { mark, picture: mark.picture, faces: mark.faces, frame: 0 };
  const frame = motionFrameIndex(motion, t - mark.t, mark.end - mark.t);
  return { mark, picture: frame === 0 ? mark.picture : motion.frames[frame - 1]!.picture, faces: animFaces(mark, motion)[frame]!, frame };
}
// A character's face on screen at t: the one placed on the frame showing at t, if any.
export function faceAt(ep: TimelineEpisode, char: string, t: number): OnFace | undefined {
  const shown = shownAt(ep, t);
  const f = shown?.faces.find(x => x.char === char);
  return shown && f ? { id: shown.mark.id, char, face: f.face } : undefined;
}
// Characters with a face on screen at t, in cast order.
export const onScreen = (ep: TimelineEpisode, t: number): string[] => resolve(ep).cast.filter(c => faceAt(ep, c, t));
export function feelingAt(ep: TimelineEpisode, char: string, t: number): Emotion {
  return latest(resolve(ep).feelings.filter(m => m.char === char), t)?.emotion ?? "neutral";
}
// A line is a voice-over when its speaker has no face on screen when it starts.
export const isVoiceOver = (ep: TimelineEpisode, line: Line & { start: number }) => line.how !== "silent" && !!line.char && !faceAt(ep, line.char, line.start);

export type MarkKind = "picture" | "camera" | "feeling";
const MARK_KEY = { picture: "pictures", camera: "camera", feeling: "feelings" } as const;
type Marks = { picture: PictureMark; camera: CameraMark; feeling: FeelingMark };
export type MarkFields<K extends MarkKind> = Omit<Marks[K], "id" | "anchor">;
// The placed markers of one kind, in time order.
export const marksOf = <K extends MarkKind>(ep: TimelineEpisode, kind: K): Placed<Marks[K]>[] => resolve(ep)[MARK_KEY[kind]] as unknown as Placed<Marks[K]>[];
export const markById = <K extends MarkKind>(ep: TimelineEpisode, kind: K, id: string) => marksOf(ep, kind).find(m => m.id === id);

// ---------------------------------------------------------------- edits

const anchorAt = (ep: TimelineEpisode, t: number): Anchor => {
  const r = resolve(ep);
  if (!r.lines.length) return { line: null, at: 0 };
  const l = lineAtTime(ep, t) ?? (t < 0 ? r.lines[0]! : r.lines[r.lines.length - 1]!);
  return { line: l.id, at: Math.max(0, Math.min(t - l.start, l.duration)) };
};

export type LineFields = Omit<Line, "id"> & { id?: string };
// A fresh silent placeholder line.
const placeholderLine = (): Line => ({ id: uid("ln"), how: "silent", status: "ready", duration: PLACEHOLDER_SECONDS, cues: [], placeholder: true });

// Inserts a line at the boundary chosen from the playhead: if t is in the first half of the line it falls in (or
// exactly at its start), before that line; otherwise after it. At or past the end: appended. Returns the new episode
// and the new line's start time. Going before line B with the playhead p seconds into it, what is on screen at the
// playhead must not move: every marker anchored to B at or before p (+0.05) is re-anchored to the new line at 0.
// With `after`, a line that t falls in always goes after it, whatever the midpoint says.
// A new line opens on the whole picture: unless a camera marker already starts it, it gets a "whole" one. The line it went
// in front of keeps the camera it had, with a marker of its own if it needs one.
export function addLine(ep: TimelineEpisode, t: number, fields: LineFields, after = false): { ep: TimelineEpisode; start: number; id: string } {
  const r = insertLine(ep, t, fields, after);
  return { ...r, ep: startWhole(r.ep, r.id) };
}
function startWhole(ep: TimelineEpisode, id: string): TimelineEpisode {
  const line = lineById(ep, id);
  const startsAt = (t: number) => marksOf(ep, "camera").some(m => Math.abs(m.t - t) < SAME);
  if (!line || !pictureAt(ep, line.start) || startsAt(line.start)) return ep;
  const cam = cameraAt(ep, line.start);
  if (cam.framing === "whole") return ep;
  const { id: _, anchor: _a, t: _t, end: _e, ...fields } = cam;
  const camera: CameraMark[] = [...ep.camera, { id: uid("ck"), anchor: { line: id, at: 0 }, framing: "whole", camera: WHOLE_CAMERA }];
  const r = resolve(ep);
  const next = r.lines[r.lines.findIndex(l => l.id === id) + 1];
  if (next && !startsAt(next.start) && pictureAt(ep, next.start)?.id === pictureAt(ep, line.start)?.id)
    camera.push({ ...fields, id: uid("ck"), anchor: { line: next.id, at: 0 } });
  return { ...ep, camera };
}
function insertLine(ep: TimelineEpisode, t: number, fields: LineFields, after: boolean): { ep: TimelineEpisode; start: number; id: string } {
  const r = resolve(ep);
  const line = { ...fields, id: fields.id ?? uid("ln") } as Line;
  const hit = lineAtTime(ep, t);
  const index = !hit ? r.lines.length : r.lines.indexOf(hit) + (after || t >= hit.start + hit.duration / 2 ? 1 : 0);
  const lines = [...ep.lines.slice(0, index), line, ...ep.lines.slice(index)];
  const start = r.lines.slice(0, index).reduce((s, l) => s + l.duration, 0);
  const before = hit && r.lines[index] === hit ? hit : undefined;
  if (!r.lines.length) { // the first line: whatever was at time 0 stays at time 0
    const first = <M extends { anchor: Anchor }>(list: M[]) => list.map(m => (m.anchor.line === null ? { ...m, anchor: { line: line.id, at: 0 } } : m));
    return { ep: { ...ep, lines, pictures: first(ep.pictures), camera: first(ep.camera), feelings: first(ep.feelings) }, start, id: line.id };
  }
  if (!before) { // appended: markers at the very end of the last line (a picture added "at the end") now start the new line
    const last = r.lines[r.lines.length - 1]!;
    const tail = <M extends { anchor: Anchor }>(list: M[]) =>
      index === r.lines.length && list.some(m => m.anchor.line === last.id && m.anchor.at >= last.duration - SAME)
        ? list.map(m => (m.anchor.line === last.id && m.anchor.at >= last.duration - SAME ? { ...m, anchor: { line: line.id, at: 0 } } : m)) : list;
    return { ep: { ...ep, lines, pictures: tail(ep.pictures), camera: tail(ep.camera), feelings: tail(ep.feelings) }, start, id: line.id };
  }
  const p = t - before.start;
  const carry = <M extends { anchor: Anchor }>(list: M[]) =>
    list.some(m => m.anchor.line === before.id && m.anchor.at <= p + SAME) ? list.map(m => (m.anchor.line === before.id && m.anchor.at <= p + SAME ? { ...m, anchor: { line: line.id, at: 0 } } : m)) : list;
  return { ep: { ...ep, lines, pictures: carry(ep.pictures), camera: carry(ep.camera), feelings: carry(ep.feelings) }, start, id: line.id };
}
// Any edit by the user makes a placeholder an ordinary line: the flag drops.
export function setLine(ep: TimelineEpisode, id: string, fields: Partial<Omit<Line, "id">>): TimelineEpisode {
  if (!ep.lines.some(l => l.id === id)) return ep;
  return { ...ep, lines: ep.lines.map(l => { if (l.id !== id) return l; const { placeholder: _, ...rest } = { ...l, ...fields }; return rest; }) };
}
// Sets an untouched placeholder's length (an animation came with its own length) and keeps it a placeholder.
// Any other line is left as it is.
export function stretchPlaceholder(ep: TimelineEpisode, lineId: string, seconds: number): TimelineEpisode {
  const l = ep.lines.find(x => x.id === lineId);
  if (!l || !l.placeholder || !(seconds > 0)) return ep;
  return { ...ep, lines: ep.lines.map(x => (x.id === lineId ? { ...x, duration: seconds } : x)) };
}
// Takes one take away from a line. If it was the one in use, the newest take left becomes the line's sound (its words and
// length with it). The last take is never taken away.
export function removeTake(ep: TimelineEpisode, lineId: string, takeId: string): TimelineEpisode {
  const l = lineById(ep, lineId);
  if (!l || !l.takes || l.takes.length < 2) return ep;
  const gone = l.takes.find(t => t.id === takeId);
  if (!gone) return ep;
  const takes = l.takes.filter(t => t.id !== takeId);
  if (gone.audio !== l.audio) return setLine(ep, lineId, { takes });
  const tk = takes[takes.length - 1]!;
  return setLine(ep, lineId, { takes, how: tk.text !== undefined ? "typed" : "recorded", audio: tk.audio, duration: tk.duration, cues: tk.cues, text: tk.text, direction: tk.direction });
}
// Swaps a line with its neighbour. Its markers move with it.
export function moveLine(ep: TimelineEpisode, id: string, dir: -1 | 1): TimelineEpisode {
  const i = ep.lines.findIndex(l => l.id === id), j = i + dir;
  if (i < 0 || j < 0 || j >= ep.lines.length) return ep;
  const lines = [...ep.lines];
  [lines[i], lines[j]] = [lines[j]!, lines[i]!];
  return { ...ep, lines };
}
// Removes a line. What showed at its end must keep showing after it, so for pictures, camera, and each character's
// feelings, the last marker inside the line (`at` more than 0.05 s in) moves to where the line was: the next line's
// start, or the end of the previous one if it was last. The earlier ones inside go, since their time is gone. A marker
// already there wins over a moved one (the inside ones are dropped). Markers at the line's start move there too, unless
// an inside one takes over. Markers at the very end of the last line (a picture added after the last line) move as well.
// If it was a picture's only line, the picture keeps a line: a fresh 1 s placeholder takes its place.
export function removeLine(ep: TimelineEpisode, id: string): TimelineEpisode {
  const i = ep.lines.findIndex(l => l.id === id);
  if (i < 0) return ep;
  const orphans = resolve(ep).pictures.filter(p => { const u = linesUnder(ep, p.id); return u.length === 1 && u[0]!.id === id; }).map(p => p.id);
  const gone = ep.lines[i]!, next = ep.lines[i + 1], prev = ep.lines[i - 1];
  const to: Anchor = next ? { line: next.id, at: 0 } : prev ? { line: prev.id, at: prev.duration } : { line: null, at: 0 };
  const inside = (m: { anchor: Anchor }) => m.anchor.line === id && m.anchor.at > SAME && !(!next && m.anchor.at >= gone.duration - SAME);
  const move = <M extends { anchor: Anchor }>(list: M[], group: (m: M) => string = () => ""): M[] => {
    if (!list.some(m => m.anchor.line === id)) return list;
    const there = (m: M) => m.anchor.line === to.line && m.anchor.line !== id && Math.abs(m.anchor.at - to.at) <= SAME;
    const promoted = new Set<M>();
    for (const g of new Set(list.filter(m => m.anchor.line === id).map(group))) {
      const mine = list.filter(m => m.anchor.line === id && group(m) === g);
      const inner = mine.filter(inside).sort((a, b) => a.anchor.at - b.anchor.at);
      const last = inner[inner.length - 1];
      const settled = list.some(m => group(m) === g && there(m)) || mine.some(m => !inside(m) && m.anchor.at > SAME);
      if (last && !settled) promoted.add(last);
    }
    const taken = new Set([...promoted].map(group));
    return list.flatMap(m => {
      if (m.anchor.line !== id) return [m];
      if (promoted.has(m)) return [{ ...m, anchor: to }];
      if (inside(m) || taken.has(group(m))) return [];
      return [{ ...m, anchor: to }];
    });
  };
  const out = { ...ep, lines: ep.lines.filter(l => l.id !== id), pictures: move(ep.pictures), camera: move(ep.camera), feelings: move(ep.feelings, m => m.char) };
  if (!orphans.length) return out;
  const ph = placeholderLine();
  return {
    ...out,
    lines: [...out.lines.slice(0, i), ph, ...out.lines.slice(i)],
    pictures: out.pictures.map(p => (orphans.includes(p.id) ? { ...p, anchor: { line: ph.id, at: 0 } } : p)),
  };
}

// Deletes a picture with its scene: every line that sits wholly inside the time it shows (a line cut by the next picture
// stays, it is partly that one's), and the camera and feeling markers anchored to those lines. A camera or feeling
// marker at the very end of the scene's last line is really the next scene's, so it moves to the next line's start.
// Later lines and their markers keep their anchors, so they just move earlier. Unlike removeLine, no placeholder is left.
export function removeScene(ep: TimelineEpisode, pictureId: string): TimelineEpisode {
  const r = resolve(ep);
  const p = r.pictures.find(m => m.id === pictureId);
  if (!p) return ep;
  const gone = new Set(r.lines.filter(l => l.start >= p.t - EPS && l.end <= p.end + EPS).map(l => l.id));
  const next = r.lines.find(l => !gone.has(l.id) && l.start >= p.end - EPS);
  const last = r.lines.filter(l => !gone.has(l.id)).at(-1);
  const to: Anchor = next ? { line: next.id, at: 0 } : last ? { line: last.id, at: last.duration } : { line: null, at: 0 };
  const lastGone = r.lines.filter(l => gone.has(l.id)).at(-1);
  const carry = <M extends { anchor: Anchor }>(list: M[]): M[] =>
    list.flatMap(m => (!m.anchor.line || !gone.has(m.anchor.line) ? [m] : lastGone && m.anchor.line === lastGone.id && m.anchor.at >= lastGone.duration - SAME && next ? [{ ...m, anchor: to }] : []));
  const rehome = <M extends { anchor: Anchor }>(list: M[]): M[] => list.map(m => (m.anchor.line && gone.has(m.anchor.line) ? { ...m, anchor: to } : m)); // (another picture at the scene's end)
  return {
    ...ep,
    lines: ep.lines.filter(l => !gone.has(l.id)),
    pictures: rehome(ep.pictures.filter(m => m.id !== pictureId)),
    camera: carry(ep.camera),
    feelings: carry(ep.feelings),
  };
}

// Adds a picture at time t. At a line boundary (at, or within 0.05 s of, a line's start or end; at the end; or in an
// empty episode) it comes with its own 1 s silent placeholder line inserted there, and starts at that line: nothing already
// anchored at the next line's start moves onto it, so it can never land on another picture. Partway into a line it is a
// cut inside that line: no placeholder. The first picture of an episode that already has lines starts at time 0 and
// plays over them, with no placeholder. Returns the new episode, the picture's id and the placeholder's id, if one was made.
export function addPicture(ep: TimelineEpisode, t: number, fields: MarkFields<"picture">, id?: string): { ep: TimelineEpisode; id: string; lineId?: string } {
  const pid = id ?? uid("pk");
  const r = resolve(ep);
  if (r.lines.length && !ep.pictures.length) {
    const mark: PictureMark = { ...fields, id: pid, anchor: { line: r.lines[0]!.id, at: 0 } };
    return { ep: { ...ep, pictures: [mark] }, id: pid };
  }
  const hit = lineAtTime(ep, t);
  const mid = hit && t > hit.start + SAME && t < hit.end - SAME;
  if (mid) return { ep: { ...ep, pictures: [...ep.pictures, { ...fields, id: pid, anchor: anchorAt(ep, t) }] }, id: pid };
  // a boundary: before the line starting at t, or at the end (after the last line)
  const index = hit ? r.lines.indexOf(hit) : t >= r.duration - SAME ? r.lines.length : 0;
  const ph = placeholderLine();
  const lines = [...ep.lines.slice(0, index), ph, ...ep.lines.slice(index)];
  const pictures = [...ep.pictures, { ...fields, id: pid, anchor: { line: ph.id, at: 0 } }];
  const empty = !r.lines.length;
  const reanchor = <M extends { anchor: Anchor }>(list: M[]) => (empty ? list.map(m => (m.anchor.line === null ? { ...m, anchor: { line: ph.id, at: 0 } } : m)) : list);
  return { ep: { ...ep, lines, pictures, camera: reanchor(ep.camera), feelings: reanchor(ep.feelings) }, id: pid, lineId: ph.id };
}

// A copy of an earlier scene's picture at the end of the episode: the same picture, faces, references, words, style and
// versions (and animations), on a marker of its own, so changing one never changes the other. Returns the new episode
// and the copy's id; with no such picture, the episode as it was.
export function copyScene(ep: TimelineEpisode, pictureId: string): { ep: TimelineEpisode; id?: string } {
  const from = ep.pictures.find(p => p.id === pictureId);
  if (!from) return { ep };
  const { id: _id, anchor: _anchor, ...fields } = from;
  const r = addPicture(ep, duration(ep), structuredClone(fields));
  return { ep: r.ep, id: r.id };
}

// Puts a marker at time t, anchored to the line containing t (at the very end: the last line at its duration). A marker
// of the same kind (and same character for feeling) within 0.05 s is replaced, keeping its place (and its id, unless
// `id` is given: a page that made the marker knows it by that id). New pictures go through `addPicture`.
export function addMarker<K extends MarkKind>(ep: TimelineEpisode, kind: K, t: number, fields: MarkFields<K>, id?: string): TimelineEpisode {
  // A picture with no picture before the playhead goes at the very start, so nothing is left bare before it.
  if (kind === "picture" && !marksOf(ep, "picture").some(m => m.t < t - SAME)) t = 0;
  const key = MARK_KEY[kind];
  const char = (fields as { char?: string }).char;
  const old = marksOf(ep, kind).find(m => Math.abs(m.t - t) < SAME && (kind === "feeling" ? (m as unknown as { char: string }).char === char : true));
  const list = ep[key] as { id: string; anchor: Anchor }[];
  const next = old
    ? list.map(m => (m.id === old.id ? { ...fields, id: id ?? m.id, anchor: m.anchor } : m))
    : [...list, { ...fields, id: id ?? uid(kind === "picture" ? "pk" : kind === "camera" ? "ck" : "fk"), anchor: anchorAt(ep, t) }];
  return { ...ep, [key]: next };
}
export function setMarker<K extends MarkKind>(ep: TimelineEpisode, kind: K, id: string, fields: Partial<MarkFields<K>>): TimelineEpisode {
  const key = MARK_KEY[kind];
  return { ...ep, [key]: (ep[key] as { id: string }[]).map(m => (m.id === id ? { ...m, ...fields } : m)) };
}
export function removeMarker(ep: TimelineEpisode, kind: MarkKind, id: string): TimelineEpisode {
  const key = MARK_KEY[kind];
  return { ...ep, [key]: (ep[key] as { id: string }[]).filter(m => m.id !== id) };
}
// Sets who is on screen with a picture, and where. The faces belong to the picture marker, so they show for as long as it does.
export function setPictureFaces(ep: TimelineEpisode, pictureId: string, faces: PlacedFace[]): TimelineEpisode {
  if (!ep.pictures.some(p => p.id === pictureId)) return ep;
  return { ...ep, pictures: ep.pictures.map(p => (p.id === pictureId ? { ...p, faces } : p)) };
}

// ---------------------------------------------------------------- animations inside a picture

// The animation a picture plays, if one is in use.
export const motionOf = (mark: PictureMark): PictureMotion | undefined =>
  mark.motionId === undefined ? undefined : mark.motions?.find(m => m.id === mark.motionId);

// Which frame (0..n-1, n = frames + 1, 0 being the picture itself) shows `s` seconds into a picture shown for `dur`.
export function motionFrameIndex(motion: PictureMotion, s: number, dur: number): number {
  const n = motion.frames.length + 1;
  if (n < 2 || !(s > 0)) return 0;
  if (motion.play === "stretch") return dur > 0 ? Math.min(n - 1, Math.floor((s * n) / dur + EPS)) : 0;
  if (!(motion.fps > 0)) return 0;
  const k = Math.floor(s * motion.fps + EPS);
  return motion.play === "loop" ? k % n : Math.min(k, n - 1);
}

type Fix = { dx: number; dy: number; k: number };
type Correction = { eyes: Fix; brows: Fix; mouth: Fix; tilt: number };
const PARTS = ["eyes", "brows", "mouth"] as const;
const NONE: Fix = { dx: 0, dy: 0, k: 1 };
const mixn = (a: number, b: number, t: number) => a + (b - a) * t;
const faceCache = new WeakMap<PictureMotion, { faces: PlacedFace[]; picture: Picture; out: PlacedFace[][] }>();

// Where every character of the picture is on every frame (n arrays, frame 0 first). Frame 0 is the picture's own faces.
// Other frames use the face found there (a character not found on a frame takes the nearest frame that has one, the
// picture's own face counting), corrected by hand: a hand-placed face says how far off the found one was, and that
// correction spreads evenly to the next hand-placed frame and holds after the last. Frame 0 is a keyframe with no
// correction. Only characters on the picture are shown. Faces are in the pixels of their own frame's picture.
export function animFaces(mark: PictureMark, motion: PictureMotion): PlacedFace[][] {
  const hit = faceCache.get(motion);
  if (hit && hit.faces === mark.faces && hit.picture === mark.picture) return hit.out;
  const n = motion.frames.length + 1, pic = mark.picture;
  const inPic = (f: PlacedFace, i: number): FaceSpots => carryFaces([f], motion.frames[i - 1]!.picture, pic)[0]!.face; // frame i's face, in the picture's pixels
  const perChar = mark.faces.map(base => {
    const c = base.char;
    const found = Array.from({ length: n }, (_, i): FaceSpots | undefined => {
      if (i === 0) return base.face;
      const f = motion.frames[i - 1]!.found.find(x => x.char === c);
      return f && inPic(f, i);
    });
    const auto = found.map((own, i) => {
      if (own) return own;
      let best = 0;
      for (let d = 1; d < n; d++) { // nearest with a face, the earlier one on a tie
        const j = [i - d, i + d].find(k => k >= 0 && k < n && found[k]);
        if (j !== undefined) { best = j; break; }
      }
      return found[best]!;
    });
    const fixes: (Correction | undefined)[] = Array.from({ length: n }, (_, i) => {
      if (i === 0) return { eyes: NONE, brows: NONE, mouth: NONE, tilt: 0 };
      const placed = motion.frames[i - 1]!.placed?.find(x => x.char === c);
      if (!placed) return undefined;
      const p = inPic(placed, i), a = auto[i]!;
      return { ...Object.fromEntries(PARTS.map(k => [k, { dx: p[k].x - a[k].x, dy: p[k].y - a[k].y, k: p[k].w / (a[k].w || 1) }])), tilt: (p.tilt ?? 0) - (a.tilt ?? 0) } as Correction;
    });
    const keys = fixes.map((f, i) => (f ? i : -1)).filter(i => i >= 0);
    const fix = (i: number): Correction => {
      const own = fixes[i];
      if (own) return own;
      const a = [...keys].reverse().find(k => k < i)!, b = keys.find(k => k > i);
      if (b === undefined) return fixes[a]!;
      const t = (i - a) / (b - a), x = fixes[a]!, y = fixes[b]!;
      return { ...Object.fromEntries(PARTS.map(k => [k, { dx: mixn(x[k].dx, y[k].dx, t), dy: mixn(x[k].dy, y[k].dy, t), k: mixn(x[k].k, y[k].k, t) }])), tilt: mixn(x.tilt, y.tilt, t) } as Correction;
    };
    return Array.from({ length: n }, (_, i): PlacedFace => {
      if (i === 0) return base;
      const a = auto[i]!, fx = fix(i);
      const on = Object.fromEntries(PARTS.map(k => [k, { x: a[k].x + fx[k].dx, y: a[k].y + fx[k].dy, w: a[k].w * fx[k].k }])) as Pick<FaceSpots, "eyes" | "brows" | "mouth">;
      const tilt = (a.tilt ?? 0) + fx.tilt;
      const face: FaceSpots = { ...on, ...(Math.abs(tilt) > 0.05 ? { tilt } : {}) };
      return carryFaces([{ char: c, face }], pic, motion.frames[i - 1]!.picture)[0]!;
    });
  });
  const out = Array.from({ length: n }, (_, i) => perChar.map(p => p[i]!));
  faceCache.set(motion, { faces: mark.faces, picture: pic, out });
  return out;
}

const withMarkOf = (ep: TimelineEpisode, pictureId: string, edit: (m: PictureMark) => PictureMark): TimelineEpisode =>
  ep.pictures.some(p => p.id === pictureId) ? { ...ep, pictures: ep.pictures.map(p => (p.id === pictureId ? edit(p) : p)) } : ep;
// Adds an animation to a picture and puts it in use.
export const addMotion = (ep: TimelineEpisode, pictureId: string, motion: PictureMotion): TimelineEpisode =>
  withMarkOf(ep, pictureId, p => ({ ...p, motions: [...(p.motions ?? []), motion], motionId: motion.id }));
export const setMotion = (ep: TimelineEpisode, pictureId: string, motionId: string, patch: Partial<Omit<PictureMotion, "id">>): TimelineEpisode =>
  withMarkOf(ep, pictureId, p => ({ ...p, motions: p.motions?.map(m => (m.id === motionId ? { ...m, ...patch } : m)) }));
export function removeMotion(ep: TimelineEpisode, pictureId: string, motionId: string): TimelineEpisode {
  return withMarkOf(ep, pictureId, p => {
    const { motionId: cur, motions: _, ...rest } = p;
    const motions = p.motions?.filter(m => m.id !== motionId);
    return { ...rest, ...(motions?.length ? { motions } : {}), ...(cur !== undefined && cur !== motionId ? { motionId: cur } : {}) };
  });
}
// Chooses which animation a picture plays; undefined puts the still picture back.
export function chooseMotion(ep: TimelineEpisode, pictureId: string, motionId: string | undefined): TimelineEpisode {
  return withMarkOf(ep, pictureId, p => {
    const { motionId: _, ...rest } = p;
    return motionId !== undefined && p.motions?.some(m => m.id === motionId) ? { ...rest, motionId } : rest;
  });
}
// Fixes the faces of one animation frame by hand (frameIndex 1..); undefined takes the fix off.
export function setFramePlaced(ep: TimelineEpisode, pictureId: string, motionId: string, frameIndex: number, placed: PlacedFace[] | undefined): TimelineEpisode {
  return setMotionFrames(ep, pictureId, motionId, frames => frames.map((f, i) => {
    if (i !== frameIndex - 1) return f;
    const { placed: _, ...rest } = f;
    return placed ? { ...rest, placed } : rest;
  }));
}
const setMotionFrames = (ep: TimelineEpisode, pictureId: string, motionId: string, edit: (f: AnimFrame[]) => AnimFrame[]) =>
  withMarkOf(ep, pictureId, p => ({ ...p, motions: p.motions?.map(m => (m.id === motionId ? { ...m, frames: edit(m.frames) } : m)) }));

// Gives a line its own copy of the image showing at its start, so it can be animated alone. A picture marker at the
// line's start copies the image (picture, words, references, style, faces, versions; no animations). If more lines
// follow under the original image, another copy of it, animations and all, starts at the next line so those lines
// look as before. Lines, feelings and camera markers are not touched. If the line already starts the image (the
// original is there), the new marker takes its place. A line that is already its image's only line returns that image
// unchanged. With no image showing, nothing changes and the id is empty.
export function splitLineOntoOwnPicture(ep: TimelineEpisode, lineId: string): { ep: TimelineEpisode; pictureId: string } {
  const line = lineById(ep, lineId);
  const orig = line && pictureAt(ep, line.start);
  if (!line || !orig) return { ep, pictureId: "" };
  const under = linesUnder(ep, orig.id);
  if (under.length === 1 && under[0]!.id === lineId) return { ep, pictureId: orig.id };
  const follows = under.find(l => l.start >= line.end - EPS);
  const { t: _t, end: _e, anchor: _a, id: _i, motions: _m, motionId: _mi, ...base } = orig;
  const copy = (id: string, anchor: Anchor, keepMotions: boolean): PictureMark => ({
    ...base, ...(keepMotions && orig.motions ? { motions: orig.motions } : {}), ...(keepMotions && orig.motionId !== undefined ? { motionId: orig.motionId } : {}),
    faces: [...base.faces], refs: [...base.refs], versions: [...base.versions], id, anchor,
  });
  const mine = copy(uid("pk"), { line: line.id, at: 0 }, false);
  const rest = follows ? [copy(uid("pk"), { line: follows.id, at: 0 }, true)] : [];
  const here = Math.abs(orig.t - line.start) < SAME; // the original starts at this line: the new picture replaces it
  return { ep: { ...ep, pictures: [...ep.pictures.filter(p => !here || p.id !== orig.id), mine, ...rest] }, pictureId: mine.id };
}

// ---------------------------------------------------------------- starting faces for a picture

type Size = { w: number; h: number };
// The faces of another picture, moved onto a picture of a different size: positions and sizes scale with the picture
// (the same size gives the same spots).
export function carryFaces(faces: PlacedFace[], from: Size, to: Size): PlacedFace[] {
  const sx = to.w / from.w, sy = to.h / from.h;
  if (sx === 1 && sy === 1) return faces.map(f => ({ ...f }));
  const spot = (s: { x: number; y: number; w: number }) => ({ x: s.x * sx, y: s.y * sy, w: s.w * sx });
  return faces.map(({ char, face }) => ({ char, face: { ...face, eyes: spot(face.eyes), brows: spot(face.brows), mouth: spot(face.mouth) } }));
}
// A starting face for each character, spread along the middle of the picture so they can be dragged into place.
export function spreadFaces(chars: string[], picture: Size): PlacedFace[] {
  return chars.map((char, i) => {
    const x = (picture.w * (i + 1)) / (chars.length + 1), y = picture.h * 0.4, w = picture.w * 0.09;
    return { char, face: { eyes: { x, y, w }, brows: { x, y: y - w * 0.4, w: w * 1.1 }, mouth: { x, y: y + w * 0.6, w: w * 0.55 } } };
  });
}

// ---------------------------------------------------------------- the script view's rows

export type ScriptBadges = { cuts: number; feelings: number }; // camera markers and feeling markers inside a line (`at` > 0.05)
export type ScriptRow =
  | { kind: "picture"; key: string; id: string; t: number; n: number; faces: string[]; after: number; motion: boolean; into?: number }
  | { kind: "line"; how: Line["how"]; placeholder: boolean; key: string; id: string; t: number; end: number; badges: ScriptBadges; voiceOver: boolean; after: number };
// `after` is where Image and Audio insert when the row is selected: the end of its line (a picture row: the end of the
// line that follows it in the list), or the end of the episode. `into`: a picture that starts partway through the line
// above it, and how many seconds in.

// The rows of the script, in time order. Every picture is a picture row. One at a line's start (and the first picture)
// comes before that line; one partway into a line B comes right after B's row, with `into` set; one at the very end of
// the last line comes after everything.
export function scriptRows(ep: TimelineEpisode): ScriptRow[] {
  const r = resolve(ep);
  const lastLine = r.lines[r.lines.length - 1];
  type Slot = { p: Placed<PictureMark>; at: "start" | "end" | "mid"; owner?: string };
  const slots: Slot[] = r.pictures.map((p, i) => {
    const l = i === 0 || p.anchor.line === null ? undefined : r.line.get(p.anchor.line);
    if (!l) return { p, at: "start", owner: r.lines[0]?.id }; // the first picture (shown from time 0) and any unanchored one start the first line
    if (p.anchor.at <= SAME) return { p, at: "start", owner: l.id };
    if (l === lastLine && p.anchor.at >= l.duration - SAME) return { p, at: "end" };
    return { p, at: "mid", owner: l.id };
  });
  const inLine = (m: { anchor: Anchor }, l: PlacedLine) => m.anchor.line === l.id && m.anchor.at > SAME && !(l === lastLine && m.anchor.at >= l.duration - SAME);
  const rows: ScriptRow[] = [];
  let n = 0;
  const pictureRow = (p: Placed<PictureMark>, after: number, into?: number): ScriptRow =>
    ({ kind: "picture", key: `pk:${p.id}`, id: p.id, t: p.t, n: ++n, faces: p.faces.map(f => f.char), after, motion: !!motionOf(p), ...(into !== undefined ? { into } : {}) });
  if (!r.lines.length) for (const s of slots) rows.push(pictureRow(s.p, 0));
  r.lines.forEach((l, k) => {
    for (const s of slots) if (s.at === "start" && s.owner === l.id) rows.push(pictureRow(s.p, l.end));
    rows.push({
      kind: "line", how: l.how, placeholder: !!l.placeholder, key: l.id, id: l.id, t: l.start, end: l.end, after: l.end,
      badges: { cuts: r.camera.filter(m => inLine(m, l)).length, feelings: r.feelings.filter(m => inLine(m, l)).length },
      voiceOver: isVoiceOver(ep, l),
    });
    for (const s of slots) if (s.at === "mid" && s.owner === l.id) rows.push(pictureRow(s.p, r.lines[k + 1]?.end ?? r.duration, s.p.t - l.start));
  });
  for (const s of slots) if (s.at === "end") rows.push(pictureRow(s.p, r.duration));
  return rows;
}
